import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";
import { GetStaticPaths } from "next";

import { SEO_SIFTED_ON_PINK_IMAGE } from "lib/constants/shared";
import { fetchNavbarAndFooter } from "lib/fetchers/common";
import { fetchCalloutsList } from "lib/fetchers/siteWide";
import { AuthorRequestQuery } from "lib/types/author";
import type { Article } from "lib/types/common";

import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";

import AuthorView from "content-ui/author";
import { fetchArticles } from "lib/fetchers/articles";

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps({ params }) {
  let page: number | null = null;

  if (
    params &&
    params.slug &&
    params.slug.length > 1 &&
    params.slug.includes("page")
  ) {
    const pageNum = parseInt(params.slug[1]);

    if (pageNum) {
      page = pageNum;
    }
  }

  let query: AuthorRequestQuery = {
    page: page || 1,
    format: "abbreviated",
    placeholderImages: false,
    taxonomy: "author",
    term_slug: params.slug[0],
  };
  const [menuItems, author, calloutListWithVariants] = await Promise.all([
    fetchNavbarAndFooter(),
    fetchArticles(query),
    fetchCalloutsList(),
  ]);

  if (!author) {
    throw new Error(`Failed to fetch article, due to an error ${author}`);
  } else if (
    author?.notFound ||
    author?.error === 404 ||
    author?.data === null
  ) {
    return { notFound: true, revalidate: 300 };
  }

  return {
    props: {
      query: query,
      slug: params.slug[0],
      canonical: buildCanonicalUrl(`/author/${params.slug.join("/")}`),
      authorData: author?.data,
      articles: author?.data,
      __initialState: {
        sitewideOption: {
          ...menuItems,
          ...calloutListWithVariants?.calloutList,
        },
      },
    },
    // we will attempt to re-generate the page:
    revalidate: 60,
  };
}

type PageProps = {
  slug: string;
  canonical: string;
  authorData: {
    author: {
      bio: string;
      name: string;
      socials: {
        email?: string | undefined;
        linkedin?: string | undefined;
        twitter?: string | undefined;
      };
    };
  };
  articles: Article[];
  query: AuthorRequestQuery;
};

const AuthorPage = (props: PageProps) => {
  const title = `${props?.authorData?.author.name} - Sifted`;
  const description = `${props?.authorData?.author?.bio}`;

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: props.canonical,
          openGraph: {
            title: title,
            description: description,
            images: [{ url: SEO_SIFTED_ON_PINK_IMAGE }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      <AuthorView
        data={{
          query: props?.query,
          slug: props?.slug,
          authorData: props?.authorData || {},
          articles: props?.articles || [],
        }}
      />
      <Footer />
    </>
  );
};

export default AuthorPage;
